import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { apiService } from '../services/api';
import { CertificateExcelReport } from '../types';
import { downloadBlob } from '../utils/fileUtils';
import { apiErrorMessage } from '../utils/apiError';

interface CertificateExcelDialogProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
  canImport: boolean;
}

export default function CertificateExcelDialog({
  open,
  onClose,
  onImported,
  canImport,
}: CertificateExcelDialogProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CertificateExcelReport | null>(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setError(null);
    if (fileRef.current) {
      fileRef.current.value = '';
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const runPreview = async (picked: File) => {
    setBusy(true);
    setError(null);
    try {
      const report = await apiService.importCertificatesExcel(picked, false);
      setPreview(report);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Vorschau fehlgeschlagen'));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const handleCommit = async () => {
    if (!file) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const report = await apiService.importCertificatesExcel(file, true);
      setPreview(report);
      onImported();
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Import fehlgeschlagen'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>Zertifikate Excel</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Vorlage herunterladen, bestehende Daten einfügen, dann prüfen. Nur .xlsx.
          Gleiche Zertifikatsnummer wird übersprungen. Schmelzland und Schmelznummer sind
          Pflicht, Schmelzwerk ist optional.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Button
            variant="outlined"
            onClick={async () => {
              const blob = await apiService.downloadCertificatesTemplate();
              downloadBlob(blob, 'lieferanten_zertifikate_vorlage.xlsx');
            }}
          >
            Vorlage
          </Button>
          <Button
            variant="outlined"
            onClick={async () => {
              const blob = await apiService.exportCertificatesExcel();
              downloadBlob(blob, 'lieferanten_zertifikate.xlsx');
            }}
          >
            Export
          </Button>
          <Button variant="contained" component="label" disabled={busy || !canImport}>
            Datei wählen
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              hidden
              onChange={(e) => {
                const picked = e.target.files?.[0];
                if (!picked) {
                  return;
                }
                const name = picked.name.toLowerCase();
                const isXlsx =
                  name.endsWith('.xlsx') ||
                  picked.type ===
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                if (!isXlsx) {
                  setError('Nur .xlsx-Dateien sind erlaubt (kein altes .xls-Format).');
                  setPreview(null);
                  setFile(null);
                  return;
                }
                setFile(picked);
                void runPreview(picked);
              }}
            />
          </Button>
        </Box>
        {preview && (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Neu {preview.created} · Aktualisiert {preview.updated ?? 0} · Duplikate {preview.skipped} ·
              Warnungen {preview.warnings} · Fehler {preview.errors}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Zeile</TableCell>
                  <TableCell>Nummer</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Hinweis</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.rows.slice(0, 80).map((row) => (
                  <TableRow key={`${row.row}-${row.certificate_number}`}>
                    <TableCell>{row.row}</TableCell>
                    <TableCell>{row.certificate_number}</TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell>{row.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {preview.rows.length > 80 && (
              <Typography variant="caption" color="text.secondary">
                Weitere {preview.rows.length - 80} Zeilen nicht angezeigt.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Schließen</Button>
        <Button
          variant="contained"
          onClick={() => void handleCommit()}
          disabled={
            busy ||
            !canImport ||
            !file ||
            !preview ||
            preview.created + (preview.updated ?? 0) === 0
          }
        >
          {busy ? 'Bitte warten…' : 'Importieren'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
